import { useKeycloak } from '@react-keycloak/web'
import { useCallback, useState } from 'react'

import { getAppBaseUrl } from '@/utils/origin'

import type { ImportEvent, ImportProgress, StructureType } from '../types'

export const useImportSql = (
	structureType: StructureType,
	structureId: number,
	structureName: string,
) => {
	const { keycloak } = useKeycloak()
	const [progress, setProgress] = useState<ImportProgress>({
		status: 'idle',
		completedSteps: [],
	})

	const importSql = useCallback(
		async (sqlFile: File) => {
			setProgress({
				status: 'importing',
				completedSteps: [],
			})

			try {
				const formData = new FormData()
				formData.append('file', sqlFile)

				const baseUrl = getAppBaseUrl()
				const encodedName = encodeURIComponent(structureName)
				const response = await fetch(
					`${baseUrl}/chainlit/api/${structureType}/${structureId}/import-sql?structure_name=${encodedName}`,
					{
						method: 'POST',
						headers: {
							Authorization: `Bearer ${keycloak.token}`,
						},
						body: formData,
					},
				)

				if (!response.ok) {
					const error = await response.json()
					throw new Error(error.detail || 'Failed to import SQL file')
				}

				// Parse SSE stream
				const reader = response.body!.getReader()
				const decoder = new TextDecoder()
				let buffer = ''
				let receivedFinalEvent = false
				let streamFinished = false

				while (!streamFinished) {
					const { done, value } = await reader.read()

					if (done) {
						if (!receivedFinalEvent) {
							throw new Error('Connection lost before import completed')
						}
						streamFinished = true
						continue
					}

					buffer += decoder.decode(value, { stream: true })
					const lines = buffer.split('\n')
					buffer = lines.pop() || ''

					for (const line of lines) {
						if (line.startsWith('data: ')) {
							try {
								const event: ImportEvent = JSON.parse(line.substring(6))

								switch (event.event) {
									case 'step_start':
										setProgress((prev) => ({
											...prev,
											currentStep: event.step_name,
											currentStepDescription: event.step_description,
										}))
										break

									case 'step_complete':
										setProgress((prev) => ({
											...prev,
											completedSteps: event.step_description
												? [...prev.completedSteps, event.step_description]
												: prev.completedSteps,
											currentStep: undefined,
											currentStepDescription: undefined,
										}))
										break

									case 'step_error':
										console.error(
											`Step ${event.step_name} failed:`,
											event.error,
										)
										break

									case 'final_success':
										receivedFinalEvent = true
										setProgress({
											status: 'success',
											completedSteps: event.completed_steps,
											totalSteps: event.total_steps,
											message: event.message,
										})
										return

									case 'final_error':
										receivedFinalEvent = true
										setProgress({
											status: 'error',
											completedSteps: event.completed_steps,
											error: event.error,
										})
										return
								}
							} catch (e) {
								console.error('Failed to parse SSE event:', line, e)
							}
						}
					}
				}
			} catch (error) {
				setProgress({
					status: 'error',
					completedSteps: [],
					error:
						error instanceof Error ? error.message : 'Unknown error occurred',
				})
			}
		},
		[keycloak.token, structureType, structureId, structureName],
	)

	const resetProgress = useCallback(() => {
		setProgress({
			status: 'idle',
			completedSteps: [],
		})
	}, [])

	return { progress, importSql, resetProgress }
}

